import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Configure axios defaults for credentials
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check auth state on mount
  useEffect(() => {
    const checkAuth = async () => {
      // Check local storage first for quick response
      const savedUser = localStorage.getItem('lunar_user');
      const savedToken = localStorage.getItem('lunar_token');
      if (savedUser && savedToken) {
        setUser(JSON.parse(savedUser));
        axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      }
      
      try {
        const token = savedToken || localStorage.getItem('lunar_token');
        if (token) {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          localStorage.setItem('lunar_user', JSON.stringify(response.data));
        }
      } catch (error) {
        console.warn('Auth validation failed, clearing local session');
        logout();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { user: userData, access_token } = response.data;
      
      // Save session
      setUser(userData);
      localStorage.setItem('lunar_user', JSON.stringify(userData));
      localStorage.setItem('lunar_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      return userData;
    } catch (error) {
      const msg = error.response?.data?.detail || 'Invalid email or password';
      throw new Error(msg);
    }
  };

  const register = async (name, email, password) => {
    try {
      const regResponse = await axios.post(`${API_URL}/auth/register`, { name, email, password });
      // Log in immediately after registration
      return await login(email, password);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Registration failed';
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (e) {
      console.error('Logout API call failed', e);
    } finally {
      setUser(null);
      localStorage.removeItem('lunar_user');
      localStorage.removeItem('lunar_token');
      delete axios.defaults.headers.common['Authorization'];
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
